import styled, { keyframes } from "styled-components";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const ButtonSpinner = styled.span`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

/** Botão "Gerar relatório" — verde Vert (#00b056), alinhado à direita na linha de filtros. */
export const ReportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  padding: 0.55rem 1rem;
  border: 1px solid var(--accent-secondary, #00b056);
  border-radius: 12px;
  background: var(--accent-secondary, #00b056);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (max-width: 640px) {
    margin-left: 0;
    width: 100%;
    justify-content: center;
  }

  &:hover:not(:disabled) {
    background: var(--accent-secondary-hover, #009647);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.75;
    cursor: wait;
  }
`;

const toneColor = {
  ok: "var(--success)",
  warning: "var(--warning)",
  critical: "var(--danger)",
  active: "var(--success)",
  paused: "var(--text-secondary)",
  /** Sem app cadastrado — configuração / entrega de push */
  no_app: "var(--warning)",
  /** Limite de envios esgotado */
  limit: "var(--danger)",
};

const toneSoft = {
  ok: "var(--success-soft)",
  warning: "var(--warning-soft)",
  critical: "var(--danger-soft)",
  active: "var(--success-soft)",
  paused: "var(--surface-glass-strong)",
  no_app: "var(--warning-soft)",
  limit: "var(--danger-soft)",
};

export const LoadBanner = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  box-sizing: border-box;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  border: 2px solid ${({ $level }) => toneColor[$level] || "var(--border-subtle)"};
  background: ${({ $level }) => toneSoft[$level] || "var(--surface-glass)"};
  color: var(--text-primary);
`;

export const LoadTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: ${({ $level }) => toneColor[$level] || "var(--text-primary)"};
`;

export const LoadText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 0.75rem;
`;

export const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.82rem;
`;

export const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-weight: 600;
`;

export const MetricBar = styled.div`
  position: relative;
  height: 8px;
  border-radius: 999px;
  background: var(--surface-glass-strong);
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: ${({ $percent }) => Math.min(100, Math.max(0, Number($percent) || 0))}%;
    background: ${({ $level }) => toneColor[$level] || "var(--accent)"};
    border-radius: inherit;
  }
`;

export const MetricDetail = styled.span`
  color: var(--text-secondary);
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.75rem;
  width: 100%;
`;

export const SummaryCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 14px;
  border: 2px solid
    ${({ $selected }) => ($selected ? "var(--accent)" : "var(--border-subtle)")};
  background: var(--surface-glass);
  color: var(--text-primary);
  cursor: pointer;
  text-align: left;

  strong {
    font-size: 1.6rem;
    line-height: 1;
  }

  span {
    font-size: 0.82rem;
    color: var(--text-secondary);
  }
`;

export const ReasonList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
`;

export const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: center;
  width: 100%;
`;

export const FilterSelect = styled.select`
  flex: 1 1 160px;
  max-width: 260px;
  padding: 0.55rem 0.75rem;
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
  background: var(--surface-glass);
  color: var(--text-primary);
  font-size: 0.85rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const ClearFiltersButton = styled.button`
  padding: 0.5rem 0.85rem;
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.82rem;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
    border-color: var(--accent);
  }
`;

export const SearchInput = styled.input`
  flex: 1 1 240px;
  padding: 0.6rem 0.9rem;
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
  background: var(--surface-glass);
  color: var(--text-primary);
  font-size: 0.9rem;
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ $tone }) => toneColor[$tone] || "var(--text-primary)"};
  background: ${({ $tone }) => toneSoft[$tone] || "var(--surface-glass)"};
`;

export const MutedText = styled.span`
  display: block;
  font-size: 0.78rem;
  color: var(--text-secondary);
`;

/** Célula Projeto + Área com rótulos distintos. */
export const ProjectAreaStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-width: 0;
  max-width: 280px;
`;

export const ProjectAreaRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  min-width: 0;
`;

export const FieldTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.4;
  color: ${({ $kind }) =>
    $kind === "area" ? "var(--accent)" : "var(--text-secondary)"};
  background: ${({ $kind }) =>
    $kind === "area"
      ? "color-mix(in srgb, var(--accent) 14%, transparent)"
      : "var(--surface-glass-strong)"};
  border: 1px solid
    ${({ $kind }) =>
      $kind === "area"
        ? "color-mix(in srgb, var(--accent) 35%, transparent)"
        : "var(--border-subtle)"};
`;

export const FieldValue = styled.span`
  display: block;
  font-size: ${({ $kind }) => ($kind === "project" ? "0.9rem" : "0.84rem")};
  font-weight: ${({ $kind }) => ($kind === "project" ? 700 : 500)};
  color: var(--text-primary);
  line-height: 1.3;
  word-break: break-word;
`;

export const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.75rem 0;
  font-size: 0.9rem;
  color: var(--text-primary);
  cursor: pointer;
`;

export const WarningNote = styled.p`
  margin: 0.5rem 0 0;
  padding: 0.6rem 0.8rem;
  border-radius: 10px;
  background: var(--warning-soft);
  color: var(--text-primary);
  font-size: 0.85rem;
`;
